<template>
  <div
    class="core-swiper"
    :class="{
      'core-swiper--ready': isReady,
      'core-swiper--has-placeholder': hasPlaceholder,
    }"
    :style="placeholderStyle"
  >
    <v-btn
      v-if="hasNavigation"
      ref="prevButtonRef"
      class="swiper-button-prev elevation-1 bg-gray-0 pa-2 rounded-circle"
      icon
    >
      <span class="ui-icon-arrow-left" />
    </v-btn>
    <v-btn
      v-if="hasNavigation"
      ref="nextButtonRef"
      class="swiper-button-next elevation-1 bg-gray-0 pa-2 rounded-circle"
      icon
    >
      <span class="ui-icon-arrow-right" />
    </v-btn>

    <div
      ref="containerRef"
      class="swiper-container"
      :class="{
        'swiper-container--no-pagination': !showPagination,
        'swiper-container--slides-auto': slidesPerView === 'auto',
      }"
    >
      <div class="swiper-wrapper">
        <slot
          name="slides"
          :slide-attrs="{ class: 'swiper-slide' }"
          :swiper-instance="swiperInstance"
        />
      </div>

      <div
        v-if="showPagination"
        ref="paginationRef"
        class="swiper-pagination mt-4"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Обёртка над Swiper: слот слайдов, опциональные стрелки и пагинация.
 *
 * При `freeMode` + `hasNavigation` стрелки листают «примерно на экран»
 * (см. `free-mode-navigation.ts`), а не через штатный Navigation.
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue'
import Swiper, { Pagination, Navigation } from 'swiper/core'
import { bindFreeModeNavigation } from 'src/core/CoreSwiper/free-mode-navigation'

Swiper.use([Pagination, Navigation])

const containerRef = ref<HTMLDivElement | null>(null)
const paginationRef = ref<HTMLDivElement | null>(null)
const nextButtonRef = ref<unknown>(null)
const prevButtonRef = ref<unknown>(null)
const swiperInstance = ref<InstanceType<typeof Swiper> | null>(null)
const freeModeNavCleanup = ref<(() => void) | null>(null)
const isReady = ref(false)

/** Ref на DOM или на Vue-компонент (`v-btn`) — Swiper ждёт HTMLElement. */
function toHTMLElement(value: unknown): HTMLElement | null {
  if (value instanceof HTMLElement) {
    return value
  }

  if (
    value
    && typeof value === 'object'
    && '$el' in value
    && (value as { $el: unknown }).$el instanceof HTMLElement
  ) {
    return (value as { $el: HTMLElement }).$el
  }

  return null
}

const props = withDefaults(defineProps<{
  hasNavigation?: boolean
  hasPagination?: boolean
  slidesPerView?: number | 'auto'
  spaceBetween?: number
  initialSlide?: number
  /** Свободный скролл без snap к слайдам (с инерцией). */
  freeMode?: boolean
  /** Отступ до первого слайда, клип остаётся по краю контейнера. */
  slidesOffsetBefore?: number
  /** Отступ после последнего слайда, клип остаётся по краю контейнера. */
  slidesOffsetAfter?: number
  /** Высота по активному слайду. Для auto + freeMode лучше выключить. */
  autoHeight?: boolean
  /**
   * Выровнять высоту всех слайдов по самому высокому.
   */
  equalSlidesHeight?: boolean
  /**
   * Резерв высоты до init Swiper на SSR и первом кадре — снижает CLS.
   */
  placeholderMinHeight?: number | string
}>(), {
  hasNavigation: false,
  slidesPerView: 1,
  spaceBetween: 8,
  initialSlide: 0,
  freeMode: false,
  slidesOffsetBefore: 0,
  slidesOffsetAfter: 0,
  placeholderMinHeight: undefined,
})

const showPagination = computed(() => props.hasPagination !== false)

const hasPlaceholder = computed(() => (
  props.placeholderMinHeight !== undefined
  && props.placeholderMinHeight !== ''
))

const placeholderStyle = computed(() => {
  if (!hasPlaceholder.value) {
    return undefined
  }

  const height = typeof props.placeholderMinHeight === 'number'
    ? `${props.placeholderMinHeight}px`
    : props.placeholderMinHeight

  return {
    '--core-swiper-placeholder-min-height': height,
  }
})

const equalizeSlidesHeight = (swiper: InstanceType<typeof Swiper>) => {
  const { slides, wrapperEl } = swiper

  if (!slides?.length || !wrapperEl) {
    return
  }

  const slideEls = Array.from(slides) as HTMLElement[]

  slideEls.forEach(slide => {
    slide.style.height = 'auto'
  })

  wrapperEl.style.height = 'auto'

  const maxHeight = slideEls.reduce(
    (currentMax, slide) => Math.max(currentMax, slide.offsetHeight),
    0,
  )

  if (!maxHeight) {
    return
  }

  const heightStyle = `${maxHeight}px`

  slideEls.forEach(slide => {
    slide.style.height = heightStyle
  })

  wrapperEl.style.height = heightStyle
}

defineExpose({
  swiperInstance,
})

onMounted(async () => {
  await nextTick()

  if (!containerRef.value) {
    return
  }

  const prevEl = toHTMLElement(prevButtonRef.value)
  const nextEl = toHTMLElement(nextButtonRef.value)
  const useFreeModeNavigation = Boolean(props.hasNavigation && props.freeMode)

  swiperInstance.value = new Swiper(
    containerRef.value,
    {
      autoHeight: props.autoHeight !== false,
      spaceBetween: props.spaceBetween,
      slidesPerView: props.slidesPerView,
      initialSlide: props.initialSlide,
      freeMode: props.freeMode,
      slidesOffsetBefore: props.slidesOffsetBefore,
      slidesOffsetAfter: props.slidesOffsetAfter,
      roundLengths: true,
      observer: true,
      observeParents: true,
      pagination: showPagination.value && paginationRef.value
        ? {
          el: paginationRef.value,
          type: 'bullets',
          dynamicBullets: true,
          dynamicMainBullets: 3,
        }
        : false,
      navigation: props.hasNavigation && !useFreeModeNavigation && prevEl && nextEl
        ? {
          nextEl,
          prevEl,
        }
        : false,
    },
  )

  if (!swiperInstance.value) {
    return
  }

  const swiper = swiperInstance.value

  if (useFreeModeNavigation && prevEl && nextEl) {
    freeModeNavCleanup.value = bindFreeModeNavigation({
      swiper,
      prevEl,
      nextEl,
    })
  }

  isReady.value = true
  await nextTick()
  swiper.update()

  if (!props.equalSlidesHeight) {
    return
  }

  const syncEqualHeight = () => equalizeSlidesHeight(swiper)

  syncEqualHeight()
  requestAnimationFrame(syncEqualHeight)
  swiper.on('resize', syncEqualHeight)
  swiper.on('update', syncEqualHeight)
  swiper.on('observerUpdate', syncEqualHeight)

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    document.fonts.ready.then(syncEqualHeight).catch(() => {})
  }
})

onBeforeUnmount(() => {
  if (freeModeNavCleanup.value) {
    freeModeNavCleanup.value()
    freeModeNavCleanup.value = null
  }

  if (!swiperInstance.value) {
    return
  }

  swiperInstance.value.destroy(true, true)
  swiperInstance.value = null
})
</script>

<style lang="scss">
@import '~swiper/swiper-bundle.min.css';
</style>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.swiper-button-next,
.swiper-button-prev {
  width: 40px;
  height: 40px;
  color: var(--lpu-branding-color-primary, $ui-kit-primary) !important;

  // v-btn анимирует opacity — без этого disabled-стрелка плавно исчезает после init
  transition-property: box-shadow, transform;

  span {
    font-size: 16px;
  }

  // Swiper ставит pointer-events: none — клик проваливается на слайд
  &.swiper-button-disabled {
    pointer-events: auto;
    cursor: default;

    :deep(.v-ripple__container) {
      display: none;
    }
  }
}

.core-swiper {
  position: relative;
  width: 100%;

  .swiper-button-next,
  .swiper-button-prev {
    span {
      font-size: 24px;
    }
  }
}

.swiper-button-prev::after,
.swiper-button-next::after {
  content: ''
}

// Стили скопированы из www/modules/SwiperModule/index.scss

.swiper-container {
  overflow-anchor: none; // фиксит странное поведение сворачивания/разворачивания элементов внутри слайдера
  overflow: hidden;
  width: 100%;
  padding-bottom: 2px;

  &--no-pagination {
    padding-bottom: 0;
  }

  & > .swiper-pagination {
    min-height: 8px;
    position: relative;
  }

  & > .swiper-pagination-bullets {
    position: relative;
    z-index: 5;
    top: initial;
    left: 50%;
    transform: translateX(-50%);
    right: initial;
    bottom: initial;
    width: 100%;
    text-align: center;
    font-size: 0;
    white-space: nowrap;

    & > :deep(.swiper-pagination-bullet) {
      margin: 0 4px;
      transition: 200ms transform, 200ms left;
    }

    & > :deep(.swiper-pagination-bullet-active) {
      background-color: $ui-kit-primary;
    }
  }

  & > .swiper-wrapper {
    display: flex;
    will-change: transform;
    box-sizing: border-box;

    & > :deep(.swiper-slide) {
      flex-shrink: 0;
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;

      @media (max-width: 480px) {
        width: 100%;
      }
    }
  }

  // slidesPerView: 'auto' — ширина из контента, иначе mobile width: 100% ломает freeMode
  &--slides-auto > .swiper-wrapper > :deep(.swiper-slide) {
    width: auto;
    max-width: none;
  }
}

.core-swiper:not(.core-swiper--ready) {
  > .swiper-container {
    overflow: hidden;
  }

  &.core-swiper--has-placeholder > .swiper-container {
    min-height: var(--core-swiper-placeholder-min-height);
    height: var(--core-swiper-placeholder-min-height);
    visibility: hidden;
  }

  &:not(.core-swiper--has-placeholder) > .swiper-container {
    height: 0;
  }

  > .swiper-button-next,
  > .swiper-button-prev {
    visibility: hidden;
  }
}
</style>
